import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled, { keyframes, css } from "styled-components";

import {
  sendVerificationEmail,
  resendVerificationEmail,
} from "../../store/slices/emailVerification/asyncThunks";
import {
  selectSendEmailState,
  selectResendEmailState,
  clearSendEmailState,
  clearResendEmailState,
} from "../../store/slices/emailVerification/slice";
import {
  FiX,
  FiMail,
  FiAlertCircle,
  FiLoader,
  FiRefreshCw,
  FiShield,
} from "react-icons/fi";
import MailChecker from "mailchecker";
import { toast } from "sonner";

const EmailVerificationModal = ({ isOpen, onClose, currentUser }) => {
  const dispatch = useDispatch();
  const [email, setEmail] = useState(currentUser?.email || "");
  const [step, setStep] = useState("input"); // 'input', 'sent', 'success'

  const sendEmailState = useSelector(selectSendEmailState);
  const resendEmailState = useSelector(selectResendEmailState);

  useEffect(() => {
    if (isOpen) {
      setEmail(currentUser?.email || "");
      // If user has email but it's not verified, show as already sent
      if (currentUser?.email && !currentUser?.emailVerified) {
        setStep("sent");
      } else {
        setStep("input");
      }
      // Clear previous states
      dispatch(clearSendEmailState());
      dispatch(clearResendEmailState());
    }
  }, [isOpen, currentUser, dispatch]);

  // Handle send email success/error
  useEffect(() => {
    if (sendEmailState.success) {
      setStep("sent");
    }
  }, [sendEmailState.success]);

  // Handle resend email success
  useEffect(() => {
    if (resendEmailState.success) {
      // Already in sent step, just show success
    }
  }, [resendEmailState.success]);

  const handleSendVerification = (e) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    e.preventDefault();

    if (!email.trim()) {
      toast.error("Molimo Vas da unesete svoju email adresu.");
      return;
    }

    if (!emailRegex.test(email)) {
      toast.error("Molimo Vas unesite važeću email adresu");

      return;
    }
    if (!MailChecker.isValid(email)) {
      toast.error("Molimo Vas unesite važeću email adresu");
      return;
    }

    dispatch(sendVerificationEmail(email));
  };

  const handleResendVerification = () => {
    dispatch(resendVerificationEmail());
  };

  const handleClose = () => {
    if (!sendEmailState.loading && !resendEmailState.loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            <FiShield />
            Verifikacija Email Adrese
          </ModalTitle>
          <CloseButton
            onClick={handleClose}
            disabled={sendEmailState.loading || resendEmailState.loading}
          >
            <FiX />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          {step === "input" && (
            <>
              <Description>
                Da biste primali obaveštenja o novostima i aktivnostima vašeg
                benda, molimo vas da verifikujete vašu email adresu.
              </Description>

              {sendEmailState.error && (
                <ErrorMessage>
                  <FiAlertCircle />
                  {sendEmailState.error}
                </ErrorMessage>
              )}

              <form onSubmit={handleSendVerification}>
                <FormGroup>
                  <Label htmlFor="email">Email Adresa</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="unesite@vasu-email.adresu"
                    disabled={sendEmailState.loading}
                    required
                  />
                </FormGroup>

                <ButtonGroup>
                  <Button
                    type="submit"
                    $primary
                    disabled={sendEmailState.loading}
                    $loading={sendEmailState.loading}
                  >
                    {sendEmailState.loading ? <FiLoader /> : <FiMail />}
                    {sendEmailState.loading
                      ? "Šalje se..."
                      : "Pošalji Verifikaciju"}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleClose}
                    disabled={sendEmailState.loading}
                  >
                    Možda kasnije
                  </Button>
                </ButtonGroup>
              </form>
            </>
          )}

          {step === "sent" && (
            <>
              {(sendEmailState.success ||
                resendEmailState.success ||
                (currentUser?.email && !currentUser?.emailVerified)) && (
                <SuccessMessage>
                  {/* <div
                    style={{
                      width: 32,
                      height: 32,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FiCheck size={16} />
                  </div> */}
                  {sendEmailState.message ||
                    resendEmailState.message ||
                    "Verifikacioni email je već poslat na vašu adresu!"}
                </SuccessMessage>
              )}

              <InfoMessage>
                {/* <div
                  style={{
                    width: 32,
                    height: 32,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FiMail size={16} />
                </div> */}
                Proverite vaš email i kliknite na link za verifikaciju. Email
                može biti u spam folderu.
              </InfoMessage>

              <Description>
                Niste dobili email? Možete ga poslati ponovo.
              </Description>

              {resendEmailState.error && (
                <ErrorMessage>
                  <div>
                    <FiAlertCircle size={16} />
                  </div>
                  {resendEmailState.error}
                </ErrorMessage>
              )}

              <ButtonGroup>
                <Button
                  type="button"
                  onClick={handleClose}
                  disabled={resendEmailState.loading}
                >
                  Zatvori
                </Button>
                <Button
                  onClick={handleResendVerification}
                  disabled={resendEmailState.loading}
                  $loading={resendEmailState.loading}
                >
                  {resendEmailState.loading ? <FiLoader /> : <FiRefreshCw />}
                  {resendEmailState.loading ? "Šalje se..." : "Pošalji Ponovo"}
                </Button>
              </ButtonGroup>
            </>
          )}
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

export default EmailVerificationModal;

// Animations
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;

  animation: ${fadeIn} 0.3s ease-out;

  padding: 1rem;
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
  border-radius: 20px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  border: 2px solid rgba(255, 107, 53, 0.3);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);

  animation: ${slideIn} 0.4s ease-out;

  position: relative;
  overflow: hidden;
  backdrop-filter: blur(15px);

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #ff6b35, #ff8c42);
    border-radius: 20px 20px 0 0;
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    margin: 1rem;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  @media (max-width: 350px) {
    flex-direction: column-reverse;
    gap: 1rem;
  }
`;

const ModalTitle = styled.h2`
  color: #ffffff;
  font-size: 1.5rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #ffffff, rgba(255, 255, 255, 0.8));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  svg {
    color: #ff6b35;
    filter: drop-shadow(0 2px 4px rgba(255, 107, 53, 0.3));
  }
  @media (max-width: 400px) {
    font-size: 1.3rem;
  }
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.7);
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 107, 53, 0.2);
    border-color: rgba(255, 107, 53, 0.4);
    color: #ff6b35;
    transform: scale(1.05);
  }
  @media (max-width: 400px) {
    height: 30px;
    width: 30px;
  }
  @media (max-width: 350px) {
    align-self: flex-end;
  }
`;

const ModalBody = styled.div`
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
`;

const Description = styled.p`
  margin-bottom: 1.5rem;
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.8);
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  color: #ff6b35;
  font-weight: 600;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.08);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  color: #ffffff;
  font-size: 1rem;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:focus {
    outline: none;
    border-color: rgba(255, 107, 53, 0.5);
    background: rgba(255, 255, 255, 0.12);
    box-shadow: 0 0 0 3px rgba(255, 107, 53, 0.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;

  flex-direction: column;
`;

const Button = styled.button`
  background: ${({ $primary }) =>
    $primary
      ? "linear-gradient(135deg, #ff6b35, #ff8c42)"
      : "rgba(255, 255, 255, 0.1)"};
  color: ${({ $primary }) =>
    $primary ? "#ffffff" : "rgba(255, 255, 255, 0.8)"};
  border: ${({ $primary }) =>
    $primary
      ? "1px solid rgba(255, 107, 53, 0.5)"
      : "1px solid rgba(255, 255, 255, 0.2)"};
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  flex: 1;
  backdrop-filter: blur(10px);

  &:hover:not(:disabled) {
    background: ${({ $primary }) =>
      $primary
        ? "linear-gradient(135deg, #ff8c42, #ffab73)"
        : "rgba(255, 255, 255, 0.15)"};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    ${({ $loading }) =>
      $loading &&
      css`
        animation: ${spin} 1s linear infinite;
      `}
  }
`;

const SuccessMessage = styled.div`
  background: rgba(46, 160, 67, 0.2);
  border: 1px solid rgba(46, 160, 67, 0.4);
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1rem;
  /* padding-left: 0.5rem; */
  display: flex;
  align-items: center;
  line-height: 1.25rem;

  gap: 0.5rem;
  color: #4caf50;

  svg {
    color: #4caf50;
  }
`;

const ErrorMessage = styled.div`
  background: rgba(244, 67, 54, 0.2);
  border: 1px solid rgba(244, 67, 54, 0.4);
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1rem;
  line-height: 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #f44336;

  svg {
    color: #f44336;
  }
`;

const InfoMessage = styled.div`
  background: rgba(33, 150, 243, 0.2);
  border: 1px solid rgba(33, 150, 243, 0.4);
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1rem;
  /* padding-left: 0.5rem; */
  line-height: 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #2196f3;

  svg {
    color: #2196f3;
  }
`;
